import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Group, GroupFilter } from '@app/models';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { BaseServiceClass } from './baseService.service';

@Injectable({ providedIn: 'root' })
export class GroupService extends BaseServiceClass<Group> {
  public groupLists: Group[] = [];
  private searchTermSubject = new BehaviorSubject<string>('');
  private groupsSubject = new BehaviorSubject<Group[]>([]);
  private groupsCache$ = new Map<string, Observable<any>>();
  private currentOffset = 0;
  private isLastPage = true;
  private initialLoadComplete = false;

  constructor(protected httpService: HttpClient) {
    super(httpService, '/groups');
    this.initializeSharedSearch();
    this.groupsSubject.next(this.groupLists);
  }

  private initializeSharedSearch(): void {
    this.searchTermSubject
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        tap(() => {
          this.currentOffset = 0;
          if (
            this.searchTermSubject.value &&
            this.searchTermSubject.value.length > 2
          ) {
            this.groupLists = [];
          }
        }),
        switchMap((searchTerm: string) => {
          const filter: GroupFilter = {
            name: searchTerm || '',
            linesPerPage: 10,
            page: 0,
            orderBy: 'name',
            direction: 'ASC',
          };
          return this.filter(filter);
        }),
        shareReplay(1)
      )
      .subscribe(response => {
        const searchTerm = this.searchTermSubject.value;

        if (searchTerm && searchTerm.length > 2) {
          this.groupLists = response.content;
        } else if (!this.initialLoadComplete || searchTerm === '') {
          this.groupLists = response.content;
          this.initialLoadComplete = true;
        } else {
          const currGroups = new Set(this.groupLists.map(group => group.id));
          this.groupLists = [
            ...this.groupLists,
            ...response.content.filter(group => !currGroups.has(group.id)),
          ];
        }

        this.currentOffset = 1;
        this.isLastPage = response.last;

        this.groupLists = this.groupLists.sort((a, b) => {
          if (a.name && b.name) {
            return a.name.localeCompare(b.name);
          }
          return 0;
        });

        this.groupsSubject.next(this.groupLists);
      });
  }

  public searchGroups(searchTerm: string): void {
    this.searchTermSubject.next(searchTerm);
  }

  public loadInitialGroups(): void {
    if (!this.initialLoadComplete) {
      this.searchGroups('');
    }
  }

  public getGroupsObservable(): Observable<Group[]> {
    return this.groupsSubject.asObservable();
  }

  public ensureGroupIsAvailable(group: any): void {
    if (group && group.id && group.name) {
      const groupExists = this.groupLists.find(g => g.id === group.id);
      if (!groupExists) {
        this.groupLists = [group, ...this.groupLists];
        this.groupsSubject.next(this.groupLists);
      }
    }
  }

  public loadNextBatch(): void {
    if (this.isLastPage) {
      return;
    }

    const groupFilter: GroupFilter = {
      name: this.searchTermSubject.value,
      linesPerPage: 10,
      page: this.currentOffset,
      orderBy: 'name',
      direction: 'ASC',
    };

    this.filter(groupFilter).subscribe(response => {
      const currentIds = new Set(this.groupLists.map(group => group.id));
      const newGroups = response.content.filter(
        group => !currentIds.has(group.id)
      );

      this.groupLists = [...this.groupLists, ...newGroups];
      this.currentOffset = this.currentOffset + 1;
      this.isLastPage = response.last;

      this.groupLists = this.groupLists.sort((a, b) => {
        if (a.name && b.name) {
          return a.name.localeCompare(b.name);
        }
        return 0;
      });

      this.groupsSubject.next(this.groupLists);
    });
  }

  public get currentSearchTerm(): string {
    return this.searchTermSubject.value;
  }

  public get isLastPageReached(): boolean {
    return this.isLastPage;
  }
}
